import { createContext, useCallback, useEffect, useState } from "react";
import axiosInstance from "../helpers/axiosInstance";

const AuthContext = createContext({});

const getStoredAuth = () => {
  if (typeof window === "undefined") return {};
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");
  const user = localStorage.getItem("user");
  if (!accessToken) return {};
  return {
    user: user ? JSON.parse(user) : null,
    accessToken,
    refreshToken,
  };
};

const getStoredOrganisation = () => {
  if (typeof window === "undefined") return {};
  const organisation = localStorage.getItem("organisation");
  return organisation ? JSON.parse(organisation) : {};
};

/** Nothing allowed: while the answer is on its way, and if it failed. */
const NO_ACCESS = {
  loaded: false,
  role: null,
  permissions: {},
  loan_approver: false,
  loan_sanctioner: false,
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(getStoredAuth);
  const [organisation, setOrganisation] = useState(getStoredOrganisation);
  const [option, setOption] = useState("dashboard");

  // What the signed-in person may do in the fund they have open.
  //
  // The console used to decide this from the role's name — ["super-admin",
  // "admin"] on every button — while the server decides it from the role's
  // permissions. "admin" is not a role any more, so a president with every
  // right saw none of the tools, and changing a role in Roles and permissions
  // changed nothing here. This asks the server the question its own guards
  // answer, as the app does, so what is shown is what will be allowed.
  const [access, setAccess] = useState(NO_ACCESS);
  const [accessVersion, setAccessVersion] = useState(0);

  useEffect(() => {
    if (!organisation?._id || !auth?.accessToken) {
      setAccess(NO_ACCESS);
      return undefined;
    }
    let live = true;
    axiosInstance
      .get(`/api/security/${organisation._id}/me`)
      .then((response) => {
        if (live) setAccess({ ...NO_ACCESS, ...response.data, loaded: true });
      })
      .catch(() => {
        // A lookup that failed must read as "nothing allowed".
        if (live) setAccess({ ...NO_ACCESS, loaded: true });
      });
    return () => {
      live = false;
    };
  }, [organisation?._id, auth?.accessToken, accessVersion]);

  /** Whether they may do `action` in `feature` — "view" is the section. */
  const can = useCallback(
    (feature, action = "view") => access.permissions?.[feature]?.[action] === true,
    [access]
  );

  /**
   * Whether they see everyone's records in a section rather than only their
   * own: view_all. Their own are always theirs to read.
   */
  const seesEveryone = useCallback((feature) => can(feature, "view_all"), [can]);

  /** The month the fund's financial year starts in, 1 to 12 (helpers/financialYear). */
  const financialYearStartMonth = Number(access.financial_year_start_month) || 4;

  const isSuperAdmin = access.role === "super-admin" || auth?.user?.role === "super-admin";

  /** Ask again — after changing a role, which may change what they may do. */
  const refreshAccess = useCallback(() => setAccessVersion((version) => version + 1), []);

  return (
    <AuthContext.Provider
      value={{
        auth,
        organisation,
        option,
        setAuth,
        setOrganisation,
        setOption,
        access,
        can,
        seesEveryone,
        financialYearStartMonth,
        isSuperAdmin,
        refreshAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
